import { Component, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { SmartGridComponent, ColDef, GridConfig, ContextMenuItem } from 'app/shared/components/smart-grid';
import { MockOrder, MOCK_ORDERS } from '../mock-data/mock-orders.data';
import { FuseConfirmationService } from '@fuse/services/confirmation';

/**
 * Context Menu Example
 * Demonstrates right-click context menu functionality with dynamic items
 */
@Component({
  selector: 'app-context-menu-example',
  standalone: true,
  imports: [CommonModule, SmartGridComponent, MatButtonModule, MatIconModule, MatSnackBarModule],
  template: `
    <div class="p-6">
      <div class="mb-4">
        <h2 class="text-2xl font-bold mb-2">Ejemplo de Context Menu (Menú Contextual)</h2>
        <p class="text-gray-600 mb-2">
          El Context Menu permite mostrar un menú personalizado al hacer <strong>click derecho</strong> sobre una fila.
          Es un feature <strong>OPT-IN</strong> que debe ser activado explícitamente.
        </p>
        <div class="flex gap-2 flex-wrap mb-4">
          <span class="px-3 py-1 bg-red-100 text-red-800 rounded-full text-sm font-semibold">
            🔒 OPT-IN Feature
          </span>
          <span class="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-semibold">
            🖱️ Right-Click Menu
          </span>
          <span class="px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-sm font-semibold">
            🎯 Dynamic Items
          </span>
          <span class="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-semibold">
            ⚡ Conditional Actions
          </span>
          <span class="px-3 py-1 bg-orange-100 text-orange-800 rounded-full text-sm font-semibold">
            🎨 Colored Actions
          </span>
        </div>

        <!-- Control Buttons -->
        <div class="flex gap-2 mb-4 flex-wrap">
          <button
            mat-raised-button
            [color]="contextMenuEnabled ? 'warn' : 'primary'"
            (click)="toggleContextMenu()">
            <mat-icon>{{ contextMenuEnabled ? 'visibility_off' : 'visibility' }}</mat-icon>
            {{ contextMenuEnabled ? 'Desactivar' : 'Activar' }} Context Menu
          </button>
          <button mat-raised-button color="accent" (click)="showInstructions()">
            <mat-icon>help</mat-icon>
            Instrucciones
          </button>
        </div>

        <!-- Status Info -->
        <div class="p-4 rounded mb-4"
             [class.bg-green-50]="contextMenuEnabled"
             [class.bg-red-50]="!contextMenuEnabled"
             [class.dark:bg-green-900]="contextMenuEnabled"
             [class.dark:bg-red-900]="!contextMenuEnabled">
          <h3 class="font-semibold mb-2 flex items-center gap-2">
            <mat-icon [class.text-green-600]="contextMenuEnabled"
                      [class.text-red-600]="!contextMenuEnabled">
              {{ contextMenuEnabled ? 'check_circle' : 'cancel' }}
            </mat-icon>
            Estado del Context Menu:
          </h3>
          <div class="text-sm">
            <strong>{{ contextMenuEnabled ? '✅ ACTIVADO' : '❌ DESACTIVADO' }}</strong>
            <p class="mt-1">
              {{ contextMenuEnabled
                ? '🖱️ Haz click derecho sobre cualquier fila para ver el menú contextual'
                : '⚠️ El menú contextual está desactivado. El click derecho muestra el menú nativo del navegador.'
              }}
            </p>
          </div>
        </div>
      </div>

      <!-- Grid -->
      <div class="bg-white dark:bg-gray-800 rounded-lg shadow">
        <app-smart-grid
          #grid
          [data]="ordersData"
          [columnDefs]="columnDefs"
          [config]="gridConfig"
          (contextMenuOpened)="onContextMenuOpened($event)"
          (contextMenuItemSelected)="onContextMenuItemSelected($event)">
        </app-smart-grid>
      </div>

      <!-- Instructions -->
      <div class="mt-4 p-4 bg-blue-50 dark:bg-blue-900 rounded">
        <h3 class="font-semibold mb-2">📋 Instrucciones:</h3>
        <ol class="text-sm space-y-2 list-decimal list-inside">
          <li><strong>Activar Context Menu:</strong> Click en el botón "Activar Context Menu" arriba</li>
          <li><strong>Abrir Menú:</strong> Haz <strong>click derecho</strong> sobre cualquier fila de la tabla</li>
          <li><strong>Opciones Dinámicas:</strong> Las opciones cambian según el estado de la orden:
            <ul class="ml-8 mt-1 list-disc list-inside">
              <li>"Aprobar" solo aparece si NO está aprobado (statusId !== 7)</li>
              <li>"Rechazar" solo aparece si NO está rechazado (statusId !== 8)</li>
              <li>"Editar" está deshabilitado para órdenes anuladas (statusId === 0)</li>
              <li>"Anular" está deshabilitado si ya está anulado (statusId === 0)</li>
            </ul>
          </li>
          <li><strong>Colores:</strong> Las acciones tienen colores según su importancia (primary, accent, warn)</li>
        </ol>
      </div>

      <div class="mt-4 p-4 bg-green-50 dark:bg-green-900 rounded">
        <h3 class="font-semibold mb-2">✨ Características del Context Menu:</h3>
        <ul class="text-sm space-y-1 list-disc list-inside">
          <li><strong>OPT-IN Feature:</strong> Desactivado por defecto, debe ser activado explícitamente</li>
          <li><strong>Right-Click Menu:</strong> Se abre con click derecho del mouse sobre una fila</li>
          <li><strong>Dynamic Items:</strong> Items condicionales por fila (disabled/hidden functions)</li>
          <li><strong>Action Colors:</strong> primary (azul), accent (púrpura), warn (rojo), default (gris)</li>
          <li><strong>Dividers:</strong> Separadores visuales entre grupos de acciones</li>
          <li><strong>Icons:</strong> Material icons para cada acción</li>
          <li><strong>Tooltips:</strong> Ayuda adicional al hacer hover (opcional)</li>
          <li><strong>Shortcuts:</strong> Muestra atajos de teclado (solo visual, no funcional)</li>
          <li><strong>Smart Positioning:</strong> Se ajusta automáticamente para no salir de la pantalla</li>
          <li><strong>Type-Safe:</strong> Full TypeScript support con genéricos &lt;T&gt;</li>
          <li><strong>Non-Intrusive:</strong> Si está desactivado, no interfiere con el menú nativo</li>
        </ul>
      </div>

      <div class="mt-4 p-4 bg-purple-50 dark:bg-purple-900 rounded">
        <h3 class="font-semibold mb-2">⚙️ Configuración del Context Menu:</h3>
        <pre class="text-xs overflow-x-auto"><code>{{configExample}}</code></pre>
      </div>

      <div class="mt-4 p-4 bg-orange-50 dark:bg-orange-900 rounded">
        <h3 class="font-semibold mb-2">🎯 Items Dinámicos (Ejemplo):</h3>
        <pre class="text-xs overflow-x-auto"><code>{{dynamicItemsExample}}</code></pre>
      </div>
    </div>
  `,
})
export class ContextMenuExampleComponent {
  @ViewChild('grid') grid!: SmartGridComponent<MockOrder>;

  contextMenuEnabled = true;

  // ============================================================================
  // DATA
  // ============================================================================

  ordersData: MockOrder[] = MOCK_ORDERS;

  columnDefs: ColDef<MockOrder>[] = [
    {
      field: 'serial',
      headerName: 'Nº Orden',
      width: 150,
    },
    {
      field: 'soldTo.name',
      headerName: 'Cliente',
      width: 220,
    },
    {
      field: 'warehouse.name',
      headerName: 'Almacén',
      width: 180,
    },
    {
      field: 'statusId',
      headerName: 'Estado',
      width: 150,
      valueFormatter: (value) => {
        const statusMap: Record<number, string> = {
          0: 'Anulado',
          1: 'Activo',
          6: 'En Progreso',
          7: 'Aprobado',
          8: 'Rechazado',
          18: 'Inactivo',
        };
        return statusMap[value] || `Estado ${value}`;
      },
    },
    {
      field: 'total',
      headerName: 'Total',
      width: 120,
      valueFormatter: (value) => `${value.toLocaleString()}`,
      cellClass: 'text-right font-semibold',
    },
    {
      field: 'createdAt',
      headerName: 'Fecha',
      width: 120,
      valueFormatter: (value) => new Date(value).toLocaleDateString(),
    },
  ];

  // Context Menu Items
  private contextMenuItems: ContextMenuItem<MockOrder>[] = [
    // Ver Detalle
    {
      label: 'Ver Detalle',
      icon: 'visibility',
      action: (order) => this.viewOrder(order),
      tooltip: 'Abrir vista detallada de la orden',
    },

    // Editar
    {
      label: 'Editar',
      icon: 'edit',
      color: 'primary',
      action: (order) => this.editOrder(order),
      disabled: (order) => order.statusId === 0, // No editar anulados
      tooltip: 'Modificar los datos de la orden',
      shortcut: 'F2',
    },

    { divider: true },

    // Cambiar Estado
    {
      label: 'Aprobar',
      icon: 'check_circle',
      color: 'accent',
      action: (order) => this.approveOrder(order),
      hidden: (order) => order.statusId === 7, // Ocultar si ya está aprobado
      tooltip: 'Aprobar esta orden',
    },
    {
      label: 'Rechazar',
      icon: 'cancel',
      action: (order) => this.rejectOrder(order),
      hidden: (order) => order.statusId === 8, // Ocultar si ya está rechazado
      tooltip: 'Rechazar esta orden',
    },

    { divider: true },

    // Acciones Adicionales
    {
      label: 'Duplicar',
      icon: 'content_copy',
      action: (order) => this.duplicateOrder(order),
      tooltip: 'Crear una copia de esta orden',
      shortcut: 'Ctrl+D',
    },
    {
      label: 'Exportar PDF',
      icon: 'picture_as_pdf',
      action: (order) => this.exportPDF(order),
      tooltip: 'Descargar orden en formato PDF',
    },
    {
      label: 'Enviar por Email',
      icon: 'email',
      action: (order) => this.sendEmail(order),
      tooltip: 'Enviar orden por correo electrónico',
    },

    { divider: true },

    // Eliminar
    {
      label: 'Anular Orden',
      icon: 'delete',
      color: 'warn',
      action: (order) => this.deleteOrder(order),
      disabled: (order) => order.statusId === 0, // Ya está anulado
      tooltip: 'Anular permanentemente esta orden',
      shortcut: 'Delete',
    },
  ];

  gridConfig: GridConfig<MockOrder> = {
    // ⚠️ CONTEXT MENU - OPT-IN FEATURE (disabled by default)
    contextMenu: {
      enabled: true, // ✅ Activar explícitamente
      items: this.contextMenuItems,
      preventDefault: true,
      stopPropagation: true,
    },
    pagination: {
      enabled: true,
      pageSize: 25,
      pageSizeOptions: [25, 50, 100],
    },
    sorting: {
      enabled: true,
      multiColumn: true,
    },
    search: {
      enabled: true,
      showSearchInput: true,
      placeholder: 'Buscar orden, cliente...',
      searchableFields: ['serial', 'soldTo.name', 'warehouse.name'],
    },
    columnFilters: {
      enabled: true,
    },
    selection: {
      enabled: true,
      mode: 'multiple',
      showCheckboxColumn: true,
    },
    styling: {
      maxHeight: '600px',
    },
  };

  constructor(
    private snackBar: MatSnackBar,
    private confirmationService: FuseConfirmationService
  ) {}

  // ============================================================================
  // CONTEXT MENU ACTIONS
  // ============================================================================

  viewOrder(order: MockOrder): void {
    this.snackBar.open(`📋 Ver detalle de: ${order.serial}`, 'Cerrar', { duration: 3000 });
  }

  editOrder(order: MockOrder): void {
    this.snackBar.open(`✏️ Editar orden: ${order.serial}`, 'Cerrar', { duration: 3000 });
  }

  approveOrder(order: MockOrder): void {
    const confirmation = this.confirmationService.open({
      title: 'Aprobar Orden',
      message: `¿Está seguro de que desea aprobar la orden ${order.serial}?`,
      icon: {
        show: true,
        name: 'heroicons_outline:check-circle',
        color: 'success',
      },
      actions: {
        confirm: {
          label: 'Sí, Aprobar',
          color: 'primary',
        },
        cancel: {
          label: 'Cancelar',
        },
      },
    });

    confirmation.afterClosed().subscribe((result) => {
      if (result === 'confirmed') {
        this.snackBar.open(`✅ Orden ${order.serial} aprobada`, 'Cerrar', { duration: 3000 });
      }
    });
  }

  rejectOrder(order: MockOrder): void {
    const confirmation = this.confirmationService.open({
      title: 'Rechazar Orden',
      message: `¿Está seguro de que desea rechazar la orden ${order.serial}?`,
      icon: {
        show: true,
        name: 'heroicons_outline:x-circle',
        color: 'warn',
      },
      actions: {
        confirm: {
          label: 'Sí, Rechazar',
          color: 'warn',
        },
        cancel: {
          label: 'Cancelar',
        },
      },
    });

    confirmation.afterClosed().subscribe((result) => {
      if (result === 'confirmed') {
        this.snackBar.open(`❌ Orden ${order.serial} rechazada`, 'Cerrar', { duration: 3000 });
      }
    });
  }

  duplicateOrder(order: MockOrder): void {
    this.snackBar.open(`📄 Duplicando orden: ${order.serial}`, 'Cerrar', { duration: 3000 });
  }

  exportPDF(order: MockOrder): void {
    this.snackBar.open(`📥 Exportando ${order.serial} a PDF...`, 'Cerrar', { duration: 3000 });
  }

  sendEmail(order: MockOrder): void {
    this.snackBar.open(`📧 Enviando ${order.serial} por email...`, 'Cerrar', { duration: 3000 });
  }

  deleteOrder(order: MockOrder): void {
    const confirmation = this.confirmationService.open({
      title: 'Anular Orden',
      message: `¿Está seguro de que desea anular la orden ${order.serial}? Esta acción no se puede deshacer.`,
      icon: {
        show: true,
        name: 'heroicons_outline:trash',
        color: 'warn',
      },
      actions: {
        confirm: {
          label: 'Sí, Anular',
          color: 'warn',
        },
        cancel: {
          label: 'No, Cancelar',
        },
      },
    });

    confirmation.afterClosed().subscribe((result) => {
      if (result === 'confirmed') {
        this.snackBar.open(`🗑️ Orden ${order.serial} anulada`, 'Cerrar', { duration: 3000 });
      }
    });
  }

  // ============================================================================
  // CONTEXT MENU EVENTS
  // ============================================================================

  onContextMenuOpened(event: any): void {
    this.snackBar.open(`Context menu abierto para: ${event.data.serial}`, 'Cerrar', {
      duration: 2000,
    });
  }

  onContextMenuItemSelected(event: any): void {
  }

  // ============================================================================
  // CONTROL METHODS
  // ============================================================================

  toggleContextMenu(): void {
    this.contextMenuEnabled = !this.contextMenuEnabled;
    this.gridConfig = {
      ...this.gridConfig,
      contextMenu: {
        ...this.gridConfig.contextMenu,
        enabled: this.contextMenuEnabled,
      },
    };

    const message = this.contextMenuEnabled
      ? '✅ Context Menu ACTIVADO - Haz click derecho en una fila'
      : '❌ Context Menu DESACTIVADO - Mostrará menú nativo';

    this.snackBar.open(message, 'Cerrar', { duration: 3000 });
  }

  showInstructions(): void {
    this.snackBar.open(
      '🖱️ Haz click derecho sobre cualquier fila para abrir el menú contextual',
      'Cerrar',
      { duration: 5000 }
    );
  }

  // ============================================================================
  // EXAMPLE CODE
  // ============================================================================

  configExample = `// Grid Configuration
gridConfig: GridConfig<Order> = {
  // ⚠️ CONTEXT MENU - OPT-IN FEATURE
  contextMenu: {
    enabled: true,  // ✅ Must be explicitly enabled
    preventDefault: true,  // Prevent native browser menu
    stopPropagation: true, // Stop event propagation
    items: [
      {
        label: 'Ver Detalle',
        icon: 'visibility',
        action: (order) => this.viewOrder(order),
        tooltip: 'Abrir vista detallada'
      },
      {
        label: 'Editar',
        icon: 'edit',
        color: 'primary',
        action: (order) => this.editOrder(order),
        disabled: (order) => order.statusId === 0,  // Dynamic disable
        shortcut: 'F2'
      },
      { divider: true },  // Separator
      {
        label: 'Anular',
        icon: 'delete',
        color: 'warn',
        action: (order) => this.deleteOrder(order),
        disabled: (order) => order.statusId === 0
      }
    ]
  }
};`;

  dynamicItemsExample = `// Dynamic Context Menu Items
items: [
  {
    label: 'Aprobar',
    icon: 'check_circle',
    color: 'accent',
    action: (order) => this.approve(order),
    // Hide if already approved
    hidden: (order) => order.statusId === 7,
    // Disable if cancelled
    disabled: (order) => order.statusId === 0
  },
  {
    label: 'Editar',
    icon: 'edit',
    color: 'primary',
    action: (order) => this.edit(order),
    // Only disable for cancelled orders
    disabled: (order) => order.statusId === 0,
    tooltip: 'Modificar los datos de la orden',
    shortcut: 'F2'
  }
]

// The menu items are evaluated per row:
// - For order with statusId=7 (approved): "Aprobar" is hidden
// - For order with statusId=0 (cancelled): "Editar" is disabled
// - For other orders: All items are visible and enabled`;
}
